<template>
  <v-dialog
    v-model="localDialog"
    :max-width="modalLayout.MD"
    v-bind="modalP2FormViewport"
    @close="close"
    @keydown.esc="close"
  >
    <v-form ref="userForm" v-model="valid" @submit.prevent="save">
      <v-card>
        <CrudFormDialogHeader
          :kicker="$t('user.management.accountCrudTitle')"
          :title="formTitle"
          show-close
          @close="close"
        />
        <v-card-text style="padding-top: 20px; font-size: 14px">
          <v-row>
            <v-col cols="12">
              <v-card flat class="ml-5">
                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding" :class="{ 'required': !editedItem.seq }">
                    <label>{{ $t('user.management.fields.userId') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-text-field
                      v-model="editedItem.userId"
                      :rules="[requiredRule]"
                      required
                      dense
                      hide-details="auto"
                      :disabled="!!editedItem.seq"
                      :readonly="!!editedItem.seq"
                    />
                  </v-col>
                </v-row>
                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding required">
                    <label>{{ $t('user.management.fields.name') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-text-field
                      v-model="editedItem.name"
                      hide-details="auto"
                      dense
                      :rules="[requiredRule]"
                    />
                  </v-col>
                </v-row>
                <v-row v-if="!item.seq" align="baseline">
                  <v-col cols="3" class="custom-padding required">
                    <label>{{ $t('user.management.fields.password') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-text-field
                      v-model="editedItem.password"
                      type="password"
                      hide-details="auto"
                      dense
                      :rules="[requiredRule]"
                    />
                  </v-col>
                </v-row>
                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding required">
                    <label>{{ $t('user.management.fields.company') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-select
                      v-model="editedItem.companySeq"
                      :items="companies"
                      item-text="companyName"
                      item-value="id"
                      hide-details="auto"
                      dense
                      :rules="[requiredSelectRule]"
                    />
                  </v-col>
                </v-row>
                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding required">
                    <label>{{ $t('user.management.fields.role') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-select
                      v-model="editedItem.userRole"
                      :items="userRoleOptions"
                      item-text="label"
                      item-value="value"
                      dense
                      :rules="[requiredRule]"
                    />
                  </v-col>
                </v-row>
                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding required">
                    <label>{{ $t('user.management.colStatus') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-select
                      v-model="editedItem.userStatus"
                      :items="userStatusOptions"
                      item-text="label"
                      item-value="value"
                      dense
                      :rules="[requiredRule]"
                    />
                  </v-col>
                </v-row>
                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding">
                    <label>{{ $t('user.management.fields.profileImage') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <ImageUploader
                      :file="profileImage"
                      :server-image-path="editedItem.userImagePath"
                      @update="handleImageUpdate"
                      @remove="handleImageRemove"
                      @error="handleImageError"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn outlined class="solvit-crud-outline-btn" @click="close">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn outlined class="solvit-crud-outline-btn solvit-crud-outline-btn--primary" type="submit">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import Api from '@/apis/Api';
import ImageUploader from '../common/ImageUploader.vue';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import {
  USER_ROLE_LABEL_I18N_KEY,
  USER_STATUS_LABEL_I18N_KEY,
  USER_ROLE_OPTION_ORDER,
  USER_STATUS_OPTION_ORDER
} from '@/utils/userI18nKeys';
import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

export default {
  name: 'UserEditDialog',
  components: {
    ImageUploader,
    CrudFormDialogHeader
  },
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      required: true
    },
    companies: {
      type: Array,
      required: true
    }
  },
  data: () => ({
    modalLayout: MODAL_MAX_WIDTH,
    modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
    valid: true,
    editedItem: {
      userId: '',
      name: '',
      password: '',
      companySeq: '',
      companyName: '',
      userImagePath: null,
      userRole: 'USER',
      userStatus: 'ACTIVE'
    },
    defaultItem: {
      userId: '',
      name: '',
      password: '',
      companySeq: '',
      companyName: '',
      userImagePath: null,
      userRole: 'USER',
      userStatus: 'ACTIVE'
    },
    editedIndex: -1,
    profileImage: null
  }),
  computed: {
    localDialog: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit('update:dialog', value);
      }
    },
    formTitle() {
      return this.item.seq
        ? this.$t('user.management.editUserDetail')
        : this.$t('user.management.addUser');
    },
    requiredRule() {
      return (v) => !!v || this.$t('common.required');
    },
    requiredSelectRule() {
      return (v) => v !== null || this.$t('common.requiredSelect');
    },
    userRoleOptions() {
      void this.$i18n.locale;
      return USER_ROLE_OPTION_ORDER.map((value) => ({
        value,
        label: this.$t(USER_ROLE_LABEL_I18N_KEY[value])
      }));
    },
    userStatusOptions() {
      void this.$i18n.locale;
      return USER_STATUS_OPTION_ORDER.map((value) => ({
        value,
        label: this.$t(USER_STATUS_LABEL_I18N_KEY[value])
      }));
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        this.initialize();
      }
    },
    item: {
      immediate: true,
      handler(newVal) {
        this.editedItem = Object.assign({}, newVal);
        console.log('this.editedItem:', newVal);
      }
    }
  },
  methods: {
    initialize() {
      this.editedItem = Object.assign({}, this.item);
    },
    close() {
      this.$emit('close');
      this.$nextTick(() => {
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation();
        }
        this.editedItem = {
          userId:'',
          name: '',
          password: '',
          companySeq: null,
          companyName:'',
          userRole: 'USER',
          userStatus: 'ACTIVE',
          profileImage: ''
        };
      });
    },
    handleImageUpdate(file) {
      this.profileImage = file;
    },
    handleImageRemove() {
      this.profileImage = null;
      this.editedItem.userImagePath = null;
    },
    handleImageError(error) {
      this.$emit('error', error);
    },
    async save() {
      if (!this.$refs.userForm.validate()) return;

      try {
        const formData = new FormData();
        Object.keys(this.editedItem).forEach(key => {
          if (this.editedItem[key] !== null && this.editedItem[key] !== undefined) {
            formData.append(key, this.editedItem[key]);
          }
        });

        if (this.profileImage) {
          formData.append('profileImage', this.profileImage);
        }

        if (this.editedItem.seq) {
          await Api.update(`/users/${this.editedItem.seq}`, formData, true);
        } else {
          await Api.create('/users', formData);
        }
        this.$emit('success');
        this.close();
      } catch (error) {
        console.error('Error saving user:', error);
        alert(this.$t('user.management.saveError'));
      }
    }
  }
};
</script>

<style scoped>
.v-dialog__content {
  max-height: 100%;
}
.v-dialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}
.custom-padding {
  padding: 8px 12px !important;
}

.required::after {
  content: "*";
  color: red;
  margin-left: 4px;
}
</style>
